/* eslint-disable max-lines */
import { Component, OnDestroy, OnInit } from '@angular/core';
import { AsyncValidatorFn, FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { cloneDeep, isEqual } from 'lodash-es';
import { Subject, of } from 'rxjs';
import {
  catchError,
  debounceTime,
  distinctUntilChanged,
  first,
  map,
  switchMap,
  takeUntil,
} from 'rxjs/operators';

import {
  Company,
  DashpivotEvent,
  EventNotifierService,
  EventTypes,
} from '@site-mate/dashpivot-shared-library';

import { IBusinessAttribute } from 'app/business-attributes/business-attribute.model';
import { GlobalApiService } from 'app/global-api/global-api.service';
import { ICompanyHomeSettings } from 'app/home/company-home-settings.model';
import { HomePageBaseComponent } from 'app/home/home-page-base.component';
import { NavService } from 'app/layout/nav.service';
import { OrgService } from 'app/org/org.service';
import { SegmentService } from 'app/segment/segment.service';
import { AppHierarchy } from 'app/shared/model/app-hierarchy.enum';
import { RegexpMatchers, validateFolderName } from 'app/shared/model/regexp-matches.model';
import { CompanyService } from 'app/shared/service/company.service';
import { ConfirmService } from 'app/shared/service/confirm.service';
import { ErrorHandler } from 'app/shared/service/error-handler.service';
import { RouteParamsService } from 'app/shared/service/route-params.service';
import { ToastrService } from 'app/shared/service/toastr.service';
import { UserService } from 'app/user/user.service';

import { IDomainModel } from '../company-domain-settings/domain.model';

@Component({
  selector: 'cc-company-general-settings',
  templateUrl: 'company-general-settings.component.html',
  styleUrls: [
    '../../home-page-settings.component.scss',
    '../../home-settings.component.scss',
    './company-general-settings.component.scss',
  ],
})
export class CompanyGeneralSettingsComponent extends HomePageBaseComponent implements OnInit, OnDestroy {
  isSuperAdmin: boolean;
  isSaving = false;
  isCompanyLoading = true;
  hasEditPermission = false;
  businessAttributesLoading = true;
  debounceTime = 500;
  company: Company;
  isVerticalRoleError: boolean;
  form: FormGroup;
  domainSettingsForm: FormGroup;
  private businessAttributesChanged: boolean;

  model: ICompanyHomeSettings = {
    name: '',
    id: '',
    workspaceId: '',
    accountId: '',
    trialExpiration: null,
    businessAttributes: [],
    jointVenture: false,
    domains: [],
    jointVentureDomains: [],
    planType: null,
    subscriptionStatus: null,
    subscriptionTerm: null,
    subscriptionInitialDate: null,
    billingCycle: null,
    paymentTermDay: null,
    paymentTermContext: null,
    paymentMethod: null,
    paymentCurrency: null,
    groupBilling: false,
    legacyRestrictions: false,
    smcDemo: false,
  };

  domainModel: IDomainModel = {
    domainInput: '',
    domainInputError: '',
    jointVentureDomainInput: '',
    jointVentureDomainInputError: '',
    parentDomainInput: '',
    parentDomainInputError: '',
    isMissingDomain: false,
  };

  private unsubscribe = new Subject<void>();
  initialFormValues: ICompanyHomeSettings;

  // Accepts a string with one or more alphanumeric characters and rejects strings with only spaces and special characters
  readonly doNotAcceptWithoutAlphanumeric = RegexpMatchers.doNotAcceptWithoutAlphanumeric;

  constructor(
    protected readonly segmentService: SegmentService,
    private readonly userService: UserService,
    private readonly companyService: CompanyService,
    private readonly errorHandler: ErrorHandler,
    private readonly toastrService: ToastrService,
    private readonly confirmService: ConfirmService,
    private readonly navService: NavService,
    private readonly activatedRoute: ActivatedRoute,
    private readonly orgService: OrgService,
    private readonly routeParamsService: RouteParamsService,
    private readonly fb: FormBuilder,
    private readonly globalApiService: GlobalApiService,
  ) {
    super(segmentService);
  }

  ngOnInit() {
    this.setupModel();
  }

  setupCompanyForm() {
    this.form = this.fb.group({
      name: [this.model.name, [Validators.required, validateFolderName(AppHierarchy.Organisation)]],
      smcDemo: [this.model.smcDemo],
      workspaceId: new FormControl({ value: this.model.workspaceId, disabled: true }),
      accountId: new FormControl(this.model.accountId, [Validators.required], [this.asyncValidateAccount()]),
    });

    this.initialFormValues = cloneDeep(this.form.value);
  }

  private asyncValidateAccount(): AsyncValidatorFn {
    return (control) =>
      of(control.value as string).pipe(
        debounceTime(this.debounceTime),
        distinctUntilChanged(),
        switchMap((value) =>
          this.globalApiService.checkWorkspaceMigrationEligibility(this.company.accountId, value),
        ),
        map(() => null),
        catchError((error: any) => {
          switch (error.status) {
            case 400:
            case 404:
              return of({ notFound: true });
            case 422:
              return of({ differentRegion: true });
            default:
              this.errorHandler.handle(error);
              return of({ failedValidation: true });
          }
        }),
        first(),
      );
  }

  hasError(controlName: string) {
    return !!this.form.get(controlName).errors;
  }

  errorMessageContent(controlName: string) {
    if (this.form.get(controlName).hasError('required')) {
      return 'Cannot be empty.';
    }
    if (this.form.get(controlName).hasError('notFound')) {
      return 'Sitemate Start Account could not be found with this ID.';
    }
    if (this.form.get(controlName).hasError('differentRegion')) {
      return 'You cannot link to a Sitemate Start Account that is set to a different data region.';
    }

    return '';
  }

  get activityLevel(): string {
    return 'org';
  }

  setupModel(): void {
    this.isSuperAdmin = this.userService.isAdmin();
    this.hasEditPermission = this.userService.isCompanyControllerOrAbove();
    this.activatedRoute.params
      .pipe(
        distinctUntilChanged(),
        switchMap((params) => this.companyService.getCompany(params.companyId)),
        takeUntil(this.unsubscribe),
      )
      .subscribe(
        (company: Company) => {
          this.company = company;
          this.model = this.filterCompanyData(company);
          this.setupCompanyForm();
          this.setUpDomainInputs(company);
          this.isCompanyLoading = false;
        },
        (error) => {
          this.errorHandler.handle(error);
        },
      );
  }

  private filterCompanyData(rawCompany: Company) {
    const company = this.companyService.getCompanyModel(rawCompany);
    const {
      id,
      workspaceId,
      accountId,
      name,
      domains,
      jointVenture,
      jointVentureDomains,
      businessAttributes,
      smcDemo,
    } = company;

    return {
      id,
      workspaceId,
      accountId,
      name,
      domains,
      jointVenture,
      jointVentureDomains,
      businessAttributes,
      smcDemo,
    } as ICompanyHomeSettings;
  }

  ngOnDestroy(): void {
    this.unsubscribe.next();
    this.unsubscribe.complete();
  }

  private getValidDomains = (domains: string) => {
    const trimAndLowerCase = (v: string) => v.trim().toLowerCase();
    return domains.split(', ').map(trimAndLowerCase).filter(Boolean);
  };

  private updateModel() {
    const { name, smcDemo } = this.form.value;
    this.model.name = name.trim();
    this.model.smcDemo = smcDemo;
    this.model.accountId = this.form.value.accountId;

    const { jointVenture } = this.model;
    const { parentDomainInput, domainInput, jointVentureDomainInput } = this.domainModel;

    this.model.domains = this.getValidDomains(jointVenture ? parentDomainInput : domainInput);
    this.model.jointVentureDomains = this.getValidDomains(jointVenture ? jointVentureDomainInput : '');
  }

  onSubmit(): void {
    this.isSaving = true;
    this.updateModel();
    const eventList = this.getEventsToNotify();

    this.orgService
      .updateCompany(this.model)
      .pipe(takeUntil(this.unsubscribe))
      .subscribe(
        (company) => {
          this.company = company;
          eventList.forEach((event) => EventNotifierService.notify(event, this.segmentService));
          this.navService.updateCompanyMetadataNav(company);
          this.toastrService.successByKey('workspaceSettingsUpdateSuccess');
          this.routeParamsService.set('company', company);

          this.resetForm();
          this.isSaving = false;
        },
        (error) => {
          error.toastKey = 'workspaceSettingsUpdateFailed';
          this.errorHandler.handle(error);
        },
      );
  }

  private getEventsToNotify(): DashpivotEvent[] {
    const events: DashpivotEvent[] = [];

    if (this.model.jointVenture !== this.company.jointVenture) {
      const context = { Context: this.model.jointVenture ? 'On' : 'Off' };
      events.push(new DashpivotEvent(EventTypes.JointVenturePropertyEdited, context));
    }

    const domainPropertyEdited =
      !isEqual(this.model.domains, this.company.domains) ||
      !isEqual(this.model.jointVentureDomains, this.company.jointVentureDomains);

    if (domainPropertyEdited) {
      events.push(new DashpivotEvent(EventTypes.DomainPropertyEdited));
    }

    return events;
  }

  private setUpDomainInputs(company: Company) {
    const domains = company.domains?.join(', ') ?? '';
    const jointVentureDomains = company.jointVentureDomains?.join(', ') ?? '';
    this.domainModel.domainInput = domains;
    this.domainModel.parentDomainInput = company.jointVenture ? domains : '';
    this.domainModel.jointVentureDomainInput = company.jointVenture ? jointVentureDomains : domains;
    this.domainModel.isMissingDomain = !this.domainModel.domainInput;
  }

  get canSaveSettings() {
    const { domainInputError, parentDomainInputError, jointVentureDomainInputError } = this.domainModel;
    const isDomainInputValid = !domainInputError && !parentDomainInputError && !jointVentureDomainInputError;

    return this.hasFormChanged && isDomainInputValid && this.form?.valid;
  }

  get hasFormChanged(): boolean {
    return (
      !isEqual(this.initialFormValues, this.form.value) ||
      this.hasDomainInputChanged ||
      this.businessAttributesChanged
    );
  }

  get hasDomainInputChanged(): boolean {
    const companyDomains = this.company.domains?.join(', ');
    if (!this.model.jointVenture) {
      return this.domainModel.domainInput !== companyDomains;
    }

    return (
      this.domainModel.parentDomainInput !== companyDomains ||
      this.domainModel.jointVentureDomainInput !== this.company.jointVentureDomains?.join(', ')
    );
  }

  onBusinessAttributesSelection(businessAttributes: IBusinessAttribute[]) {
    this.model.businessAttributes = businessAttributes.map((ba) => ba._id);
    this.businessAttributesChanged = true;
  }

  resetForm(): void {
    this.initialFormValues = cloneDeep(this.form.value);
  }

  onSMCDemoClick(event: Event) {
    if (!this.isSuperAdmin) {
      event.preventDefault();
    }
  }

  deleteWorkspace() {
    if (!this.isSuperAdmin) {
      return;
    }

    const currentCompanyId = this.model.id;
    this.confirmService
      .confirmDelete('deleteCompany')
      .then(() => {
        return this.orgService
          .removeCompany(currentCompanyId)
          .pipe(takeUntil(this.unsubscribe))
          .subscribe(
            () => {
              this.company.archived = true;
              this.toastrService.successByKey('companyDeleted');
              this.routeParamsService.set('company', this.company);
            },
            (error) => this.errorHandler.handle(error),
          );
      })
      .catch(() => {});
  }

  businessAttributesLoaded() {
    this.businessAttributesLoading = false;
  }

  changeOrgName(event: string) {
    this.form.get('name').setValue(event.trim());
    this.form.get('name').markAsTouched();
  }

  changeAccountId(value: string) {
    this.form.get('accountId').setValue(value.trim());
    this.form.get('accountId').markAsTouched();
  }
}
